import React, { useState, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import Overflow from 'react-overflow-indicator';
import styled, { css } from 'styled-components';

import Text from '~/shared/components/Text';
import { PersonalizationTypes, StateFields } from '~/shared/utils/embroidery';
import {
  Button,
  mobile,
  ProductImage,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import EmbroideryPreviewImage from '../EmbroideryPreviewImage';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import SlidingSelector from '../SlidingSelector';

const imageBreakpoints = [{ minWidth: 0, pictureWidth: 208 }];

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.textDefault};
`;

const Title = styled(Text).attrs({ variant: 'labelUnstyled' })`
  font-weight: 700;
`;

const FeeText = styled(Text).attrs({ as: 'span', variant: 'bodyMed' })``;

const PreviewPanel = styled.div`
  flex: 1 0 50%;
`;

const ProductImageContainer = styled.div`
  position: relative;
`;

const EmbroideryPreviewContainer = styled.div`
  width: 210px;
  min-height: 120px;
  position: absolute;
  border: 2px dashed ${({ theme: { colors } }) => colors.white};
  top: calc(50% - 90px);
  right: calc(50% - 105px);
  padding: 32px;
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 8px;
  padding: 0 8px;
  width: 100%;
`;

const StyledSelector = styled(SlidingSelector)`
  margin: 0;
`;

const EmbroideryIconSelectionWrapper = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.sublabelBlack};

  ${mobile`
    flex-direction: column;
  `}
`;

const CustomizationPanel = styled.div`
  flex: 1 0 50%;
  display: flex;
  flex-direction: column;
  padding: 32px 56px;

  ${mobile`
    padding: 16px 24px 24px;
  `}
`;

const StyledEmbroideryTextForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContinueButton = styled(Button).attrs({ variant: 'primary' })`
  align-self: stretch;
  margin-top: 40px;

  ${mobile` 
    margin-top: 16px;
  `}
`;

const SkipButton = styled(Button).attrs({ variant: 'secondary' })`
  align-self: stretch;
  margin-top: 16px;
  color: ${({ theme: { colors } }) => colors.textDefault};
`;

const AlertCircle = styled(FaExclamationCircle)`
  color: ${({ theme: { colors } }) => colors.palette.white};
  margin-right: 8px;
`;

const AlertMessage = styled.p`
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: ${({ theme: { colors } }) => colors.white};
  ${({ theme: { typography } }) => typography.variants.bodyMed};
`;

const SelectedPill = styled.div`
  display: inline-block;
  align-self: ${({ $pos }) => ($pos === 1 ? 'flex-start' : 'flex-end')};
  padding: 4px 16px;
  border-radius: 16px;
  margin-bottom: 7.5px;
  ${({ theme }) => css`
    ${theme.typography.variants.labelUnstyled};
    font-weight: 700;
    color: ${theme.colors.textFadedAlternate};
    background: ${theme.colors.backgroundLight};
    border: 2px solid ${theme.colors.textFadedAlternate};
  `}
`;

const LogoContainer = styled(Overflow)`
  max-height: 328px;

  ${mobile`
    max-height: 208px;
  `}

  ${({ $canScroll }) => {
    const boxShadow = [];

    if ($canScroll.up) {
      boxShadow.push('0px 4px 0px 0px rgba(0,0,0,0.25) inset');
    }

    if ($canScroll.down) {
      boxShadow.push('0px -4px 0px 0px rgba(0,0,0,0.25) inset');
    }

    return css`
      box-shadow: ${boxShadow.join(',')};
    `;
  }}
`;

const LogoGrid = styled.div`
  display: grid !important;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 88px;
  gap: 8px;
`;

const Logo = styled(Button).attrs({ variant: 'unstyled' })`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius};

  ${({ $selected }) =>
    $selected &&
    css`
      background: ${({ theme }) => theme.colors.backgroundDarkAlternate};
      color: ${({ theme }) => theme.colors.textLight};
    `}
`;

const logoPersonalizationRbs = {
  1: (
    <FormattedMessage
      id="site_pdp.embroidery_location_left_shoulder"
      defaultMessage="Left Shoulder"
    />
  ),
  2: (
    <FormattedMessage
      id="site_pdp.embroidery_location_right_shoulder"
      defaultMessage="Right Shoulder"
    />
  ),
};

export default function EmbroideryIconSelection({
  handleEmbroideryPreview,
  iconOptions,
  locationOptions,
  onCTAClick,
  onSkip,
  previewError,
  previewImages,
  previewLoading,
  iconCustomizations,
}) {
  const { iconPersonalizations } = useProductDetailEmbroideryContext();

  const makePreviewKey = useCallback((item, locationId) => {
    if (!item) {
      return null;
    }

    return `${item.optionId}::${item.name}::${locationId}`;
  }, []);

  const [{ selectedIcon, selectedIconLocation, previewKey }, setIconSelection] =
    useState({
      selectedIcon: iconCustomizations.icon,
      selectedIconLocation: iconCustomizations.iconLocation,
      previewKey: makePreviewKey(
        iconCustomizations.icon,
        iconCustomizations.iconLocation
      ),
    });

  const options = iconOptions[selectedIconLocation];

  const [{ canScroll }, setCanScroll] = useState({ canScroll: { down: true } });

  const locations = useMemo(() => {
    return locationOptions.map((locationId) => {
      return {
        tab: parseInt(locationId, 10),
        content: logoPersonalizationRbs[locationId],
      };
    });
  }, [locationOptions]);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    onCTAClick({
      [StateFields.ICON_CUSTOMIZATIONS]: {
        [StateFields.ICON]: selectedIcon,
        [StateFields.ICON_LOCATION]: selectedIconLocation,
      },
    });
  };

  const handleIconClick = (icon) => {
    const chosen =
      selectedIcon && selectedIcon.optionId === icon.optionId ? null : icon;
    const previewKey = makePreviewKey(chosen, selectedIconLocation);

    setIconSelection({
      previewKey,
      selectedIconLocation,
      selectedIcon: chosen,
    });

    if (chosen && !previewImages[previewKey]) {
      handleEmbroideryPreview({
        previewKey,
        embroideryOptions: {
          logo: chosen.optionId,
          masterProductId: iconPersonalizations.productId,
          template: iconPersonalizations.templateLabel,
        },
      });
    }
  };

  const handleIconLocationClick = (value) => {
    // reset selected icon anytime the icon location changes
    setIconSelection({
      selectedIconLocation: value,
      selectedIcon: null,
      previewKey: '',
    });
  };

  return (
    <EmbroideryIconSelectionWrapper>
      <PreviewPanel>
        <ProductImageContainer>
          <ProductImage
            breakpoints={imageBreakpoints}
            disableHover
            type="default"
          />
          <EmbroideryPreviewContainer>
            <EmbroideryPreviewImage
              isLoading={previewLoading}
              error={previewError}
              previewImageSrc={previewImages[previewKey]}
            />
          </EmbroideryPreviewContainer>
          <SelectorContainer>
            {selectedIcon && (
              <SelectedPill $pos={selectedIconLocation}>
                <FormattedMessage
                  id="site_pdp.embroidery_sleeve_tag_line_item"
                  defaultMessage="Sleeve Tag"
                />
                {': '}
                {selectedIcon.name}
              </SelectedPill>
            )}
            <StyledSelector
              name="location"
              selectionOptions={locations}
              selectedTabId={selectedIconLocation}
              buttonAutotag="location"
              listAutotag="location-dropdown"
              onOptionSelect={handleIconLocationClick}
            />
            {locationOptions.length === 1 && (
              <AlertMessage>
                <AlertCircle />
                <FormattedMessage
                  id="site_pdp.embroidery_alert_message"
                  defaultMessage="Embroidery only available on {location}."
                  values={{ location: locations[0].content }}
                />
              </AlertMessage>
            )}
          </SelectorContainer>
        </ProductImageContainer>
      </PreviewPanel>

      <CustomizationPanel>
        <TitleContainer>
          <Title>
            <FormattedMessage
              id="site_pdp.embroidery_choose_your_sleeve_tag"
              defaultMessage="Choose Your Sleeve Tag"
            />
          </Title>
          <FeeText>
            <FormattedMessage
              id="site_pdp.embroidery_flat_fee"
              defaultMessage="$5 Flat Fee"
            />
          </FeeText>
        </TitleContainer>
        <LogoContainer
          onStateChange={setCanScroll}
          tolerance={1}
          $canScroll={canScroll}
        >
          <Overflow.Content>
            <LogoGrid>
              {options?.map((option) => (
                <Logo
                  key={`${option.label}-${option.optionId}`}
                  onClick={() => {
                    handleIconClick({
                      optionId: option.optionId,
                      name: option.label,
                    });
                  }}
                  $selected={
                    selectedIcon && selectedIcon.optionId === option.optionId
                  }
                >
                  {option.label}
                </Logo>
              ))}
            </LogoGrid>
          </Overflow.Content>
        </LogoContainer>
        <StyledEmbroideryTextForm onSubmit={handleSubmit}>
          <ContinueButton
            type="submit"
            size="md"
            disabled={!selectedIconLocation || !selectedIcon}
            data-autotag="pdp_embroidery_confirm_btn"
          >
            <FormattedMessage
              id="site_pdp.emboridery_continue"
              defaultMessage="Save and Continue"
            />
          </ContinueButton>
        </StyledEmbroideryTextForm>
        <SkipButton
          onClick={() => onSkip(PersonalizationTypes.LOGO)}
          size="md"
          data-autotag="pdp_embroidery_confirm_btn"
        >
          <FormattedMessage
            id="site_pdp.emboridery_skip_icon"
            defaultMessage="Skip Sleeve Tag"
          />
        </SkipButton>
      </CustomizationPanel>
    </EmbroideryIconSelectionWrapper>
  );
}

EmbroideryIconSelection.propTypes = {
  handleEmbroideryPreview: PropTypes.func,
  iconCustomizations: PropTypes.shape({
    icon: PropTypes.shape({
      optionId: PropTypes.number,
      name: PropTypes.string,
    }),
    iconLocation: PropTypes.number,
  }),
  iconOptions: PropTypes.shape({
    1: PropTypes.arrayOf(
      PropTypes.shape({
        optionId: PropTypes.number,
        label: PropTypes.string,
      })
    ),
    2: PropTypes.arrayOf(
      PropTypes.shape({
        optionId: PropTypes.number,
        label: PropTypes.string,
      })
    ),
  }),
  locationOptions: PropTypes.arrayOf(PropTypes.string),
  onCTAClick: PropTypes.func,
  onSkip: PropTypes.func,
  previewError: PropTypes.bool,
  previewImages: PropTypes.object,
  previewLoading: PropTypes.bool,
};
